"use client"

import { Variants } from "framer-motion"

/**
 * Standard scroll animation configuration to use with useInView
 * The margin property delays the animation until the element is 
 * scrolled past the specified amount
 */
export const standardScrollConfig = {
  once: true,
  amount: 0.1,
  margin: "-100px 0px"
}

/**
 * Animation variants for staggered fade-in effects
 */
export const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2,
    },
  },
}

/**
 * Animation variants for text elements that fade in and slide up
 */
export const textVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants for images that fade in and scale up
 */
export const imageVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      delay: 0.4,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants for cards that fade in and slide up
 */
export const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants for section headings
 */
export const headingVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants for elements that fade in from left
 */
export const fromLeftVariants: Variants = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants for elements that fade in from right
 */
export const fromRightVariants: Variants = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
}

/**
 * Animation variants with staggered children for grid layouts
 */
const gridContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

/**
 * Animation variants for button elements with hover effect
 */
export const buttonVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
}

/**
 * Helper function to create a useInView config with a custom delay
 * @param marginTop - Distance from the top of the viewport to trigger animation (in px)
 * @param amount - Portion of the element that must be visible (0-1)
 * @returns UseInViewOptions object
 */
export function createScrollConfig(marginTop: number = -100, amount: number = 0.1) {
  return {
    once: true,
    amount: amount,
    margin: `${marginTop}px 0px`
  }
}